import { Cursor, CursorBody, CursorName, CursorPointer } from '@/components/cursor';
import { cn } from '@/lib/utils';
import type { CursorPosition } from '@/types/collaboration';

interface CollaborativeCursorProps {
  userId: string;
  userName: string;
  userAvatar: string | null;
  position: CursorPosition;
  color?: string;
  showAvatar?: boolean;
}

// Generate consistent color from user ID
function getUserColor(userId: string): string {
  const colors = [
    'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950/50',
    'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-950/50',
    'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-950/50',
    'text-pink-600 bg-pink-50 dark:text-pink-400 dark:bg-pink-950/50',
    'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-950/50',
    'text-cyan-600 bg-cyan-50 dark:text-cyan-400 dark:bg-cyan-950/50',
  ];

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash = hash & hash;
  }

  return colors[Math.abs(hash) % colors.length];
}

export function CollaborativeCursor({
  userId,
  userName,
  userAvatar,
  position,
  color,
  showAvatar = true,
}: CollaborativeCursorProps) {
  const userColor = color || getUserColor(userId);
  const [textColor, bgColor] = userColor.split(' bg-');

  return (
    <Cursor
      className="absolute transition-all duration-200 ease-out pointer-events-none z-50"
      style={{
        top: `${position.y}%`,
        left: `${position.x}%`,
      }}
    >
      <CursorPointer className={textColor} />
      <CursorBody className={cn(bgColor, textColor, 'gap-1 px-3 py-2 shadow-lg border border-border/20 min-w-max')}>
        <div className="flex items-center gap-2 whitespace-nowrap">
          {showAvatar && userAvatar && (
            <img
              alt={userName}
              src={userAvatar}
              className="rounded-full size-4 flex-shrink-0"
              width={16}
              height={16}
            />
          )}
          <CursorName className="font-medium text-xs whitespace-nowrap">{userName}</CursorName>
        </div>
      </CursorBody>
    </Cursor>
  );
}
